import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Tabs,
    Tab,
    Avatar,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    Phone as PhoneIcon,
    Email as EmailIcon,
    LocationOn as LocationIcon,
    Cake as CakeIcon,
    Work as WorkIcon,
    Business as BusinessIcon,
    Assessment as AssessmentIcon
} from '@mui/icons-material';
import {encryptText,encryptAppId,decryptText,decryptAppId,} from "../utils/encryption";


// Tab Panel component
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`assessor-tabpanel-${index}`}
      aria-labelledby={`assessor-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const AssessorProfile = () => {
  const location = useLocation();
  // Function to get orgId from URL query parameters with decryption
    const getAssessorIdFromQuery = () => {
      const searchParams = new URLSearchParams(location.search);
      const encryptedAssessorId = searchParams.get("AssessorId");
      if (encryptedAssessorId) {
        try {
          const decryptedAssessorId = decryptText(encryptedAssessorId);
          console.log("📍 Using decrypted assessorId from URL query:", decryptedAssessorId);
          return decryptedAssessorId;
        } catch (error) {
          return encryptedAssessorId;
        }
      }
      return null;
    };
  
    const getAssessorId = () => {
      const queryAssessorId = getAssessorIdFromQuery();
      if (queryAssessorId) {
        return queryAssessorId;
      }
      return null;
    };
  
  const [assessorId, setAssessorId] = useState(getAssessorId());
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assessorData, setAssessorData] = useState(null);
  const [fieldsWithExpertise, setFieldsWithExpertise] = useState([]);

  // Fetch assessor profile and expertise data
  useEffect(() => {
    const fetchAssessorData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch profile data
        const profileUrl = `${process.env.REACT_APP_API_URL}/assessor/assessor-profile?assessorId=${assessorId}`;
        const profileResp = await axios.get(profileUrl);
        
        if (profileResp.data && profileResp.data.isstatus && profileResp.data.AssessorProfile.length > 0) {
          const profile = profileResp.data.AssessorProfile[0];
          
          // Parse lab types from comma-separated string
          const labTypes = profile.LabType 
            ? profile.LabType.split(',').map(type => type.trim()).filter(type => type)
            : [];
          
          // Parse standards from comma-separated string
          const standards = profile.Standards && profile.Standards !== 'N/A'
            ? profile.Standards.split(',').map(std => std.trim()).filter(std => std)
            : [];

          setAssessorData({
            id: assessorId,
            name: profile.AssessorName || '',
            mobile: profile.Mobile || '',
            email: profile.Email || '',
            address: profile.Address || '',
            dob: profile.DOB || '',
            photo: profile.Photo || '',
            expertise: labTypes,
            capacity: profile.AssessorCapacity || '',
            status: profile.WorkStatus || '',
            organization: profile.CurrentEmployer || '',
            standards: standards,
            loginName: profile.LoginName || '',
            totalAssessments: profile.TotalAssessment || 0,
            totalPre: profile.TotalPre || 0,
            totalPreOngoing: profile.TotalPreOngoing || 0,
            totalPreCompleted: profile.TotalPreCompleted || 0,
            totalMain: profile.TotalMain || 0,
            totalMainOngoing: profile.TotalMainOngoing || 0,
            totalMainCompleted: profile.TotalMainCompleted || 0,
            remarks: profile.Remarks || 'N/A'
          });
        } else {
          setError('Failed to fetch assessor profile');
        }

        // Fetch expertise data
        const expertiseUrl = `${process.env.REACT_APP_API_URL}/assessor/assessor-expertise?assessorId=${assessorId}`;
        const expertiseResp = await axios.get(expertiseUrl);
        
        if (expertiseResp.data && expertiseResp.data.isstatus && expertiseResp.data.AssessorExpertise) {
          const expertise = expertiseResp.data.AssessorExpertise.map(item => ({
            field: item.LabType || '',
            discipline: item.DisciplineName || '',
            group: item.GroupName || '',
            remarks: item.ExpertiseRemarks || 'N/A'
          }));
          setFieldsWithExpertise(expertise);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching assessor data:', err);
        setError('Unable to fetch assessor data. Please try again.');
        setLoading(false);
      }
    };

    if (assessorId) {
      fetchAssessorData();
    }
  }, [assessorId]);

  const assessmentStats = {
    total: assessorData?.totalAssessments || 0,
    pre: {
      total: assessorData?.totalPre || 0,
      ongoing: assessorData?.totalPreOngoing || 0,
      completed: assessorData?.totalPreCompleted || 0
    },
    main: {
      total: assessorData?.totalMain || 0,
      ongoing: assessorData?.totalMainOngoing || 0,
      completed: assessorData?.totalMainCompleted || 0
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getFieldColor = (field) => {
    const colors = {
      'Testing': 'primary',
      'Calibration': 'secondary',
      'Medical': 'error',
      'PTP': 'warning',
      'RMP': 'info'
    };
    return colors[field] || 'default';
  };

  return (
    <Box sx={{ p: 2, mt: 10, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Content - Only show when data is loaded */}
      {!loading && !error && assessorData && (
        <>
      {/* Header Section */}
      <Card sx={{ mb: 2, boxShadow: 3, borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar 
                src={assessorData.photo ? `https://nabl7t.s3.ap-south-1.amazonaws.com/live/${assessorData.photo}` : undefined}
                sx={{ 
                  width: 80, 
                  height: 80, 
                  bgcolor: 'primary.main',
                  fontSize: '2rem'
                }}
              >
                {!assessorData.photo && assessorData.name.split(' ').map(n => n[0]).join('')}
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {assessorData.name}
                </Typography>
                <Typography variant="h6" color="primary" sx={{ mb: 0.5 }}>
                  {assessorData.capacity}
                </Typography>
                <Chip 
                  label={assessorData.status} 
                  color="success" 
                  size="small"
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PhoneIcon color="action" sx={{ mr: 2 }} />
                <Typography variant="body1">
                  <strong>Mobile:</strong> {assessorData.mobile}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmailIcon color="action" sx={{ mr: 2 }} />
                <Typography variant="body1">
                  <strong>Email:</strong> {assessorData.email}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CakeIcon color="action" sx={{ mr: 2 }} />
                <Typography variant="body1">
                  <strong>DOB:</strong> {assessorData.dob}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <LocationIcon color="action" sx={{ mr: 2, mt: 0.5 }} />
                <Typography variant="body1">
                  <strong>Address:</strong> {assessorData.address}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon color="action" sx={{ mr: 2 }} />
                <Typography variant="body1">
                  <strong>Organization:</strong> {assessorData.organization}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <WorkIcon color="action" sx={{ mr: 2 }} />
                <Typography variant="body1">
                  <strong>ID:</strong> {assessorData.loginName}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Expertise and Standards */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
              Expertise
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {assessorData?.expertise && assessorData.expertise.length > 0 ? (
                assessorData.expertise.map((exp, index) => (
                  <Chip 
                    key={index}
                    label={exp} 
                    color={getFieldColor(exp)}
                    variant="outlined"
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No expertise data available
                </Typography>
              )}
            </Box>

            <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
              Standards
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {assessorData?.standards && assessorData.standards.length > 0 ? (
                assessorData.standards.map((standard, index) => (
                  <Chip 
                    key={index}
                    label={standard} 
                    color="primary"
                    variant="filled"
                    size="small"
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No standards data available
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      </>
      )}
    </Box>
  );
};

export default AssessorProfile;